import React from "react";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useSignedThumbnailUrls, resolveThumbnailUrl } from "@/hooks/useSignedThumbnailUrls";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Search,
  X,
  Plus,
  Image as ImageIcon,
  Video,
  ArrowUp,
  ArrowDown,
  Sparkles,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import type { FlexibleAsset, FlexibleData, FlexibleAssetType } from "@/types/bulkAd";

interface CreativeAsset {
  id: string;
  filename: string;
  meta_creative_id: string;
  dimensions: string | null;
  thumbnail_url: string | null;
  thumbnail_path: string | null;
  aspect_ratio_category: string | null;
  creative_type: string;
}

interface FlexibleAssetsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  adAccountId: string;
  userId: string;
  existingData: FlexibleData | null;
  onSave: (data: FlexibleData) => void;
}

export function FlexibleAssetsDialog({
  isOpen,
  onClose,
  adAccountId,
  userId,
  existingData,
  onSave,
}: FlexibleAssetsDialogProps) {
  const [assets, setAssets] = useState<CreativeAsset[]>([]);
  const [selectedAssets, setSelectedAssets] = useState<FlexibleAsset[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Initialize from existing data
  useEffect(() => {
    if (isOpen) {
      if (existingData?.assets?.length) {
        setSelectedAssets(existingData.assets);
      } else {
        setSelectedAssets([]);
      }
      setSearchQuery("");
    }
  }, [isOpen, existingData]);

  // Fetch ALL assets (both images and videos)
  useEffect(() => {
    async function fetchAssets() {
      if (!adAccountId || !userId) return;

      setLoading(true);

      const { data, error } = await supabase
        .from("creative_metadata")
        .select("id, filename, meta_creative_id, dimensions, thumbnail_url, thumbnail_path, aspect_ratio_category, creative_type")
        .eq("user_id", userId)
        .eq("meta_account_id", adAccountId)
        .in("creative_type", ["image", "video"])
        .or('thumbnail_url.neq.,thumbnail_path.neq.')
        .order("created_at", { ascending: false });

      if (error) {
        console.error("[FlexibleAssetsDialog] Error fetching assets:", error);
        setLoading(false);
        return;
      }

      setAssets(data || []);
      setLoading(false);
    }

    if (isOpen) {
      fetchAssets();
    }
  }, [adAccountId, userId, isOpen]);

  // Filter by search
  const filteredAssets = useMemo(() => {
    if (!debouncedSearch) return assets;
    const query = debouncedSearch.toLowerCase();
    return assets.filter((a) => a.filename.toLowerCase().includes(query));
  }, [assets, debouncedSearch]);

  // Generate signed URLs for filtered assets
  const assetPaths = useMemo(() => filteredAssets.map(a => a.thumbnail_path), [filteredAssets]);
  const { urlMap: signedUrlMap } = useSignedThumbnailUrls(assetPaths);

  // Toggle asset selection
  const toggleAsset = useCallback((asset: CreativeAsset) => {
    setSelectedAssets((prev) => {
      const exists = prev.find((a) => a.id === asset.id);
      if (exists) {
        return prev.filter((a) => a.id !== asset.id);
      } else {
        if (prev.length >= 10) return prev;
        return [
          ...prev,
          {
            id: asset.id,
            meta_creative_id: asset.meta_creative_id,
            filename: asset.filename,
            thumbnail_url: asset.thumbnail_url,
            type: asset.creative_type as FlexibleAssetType,
            aspect_ratio_category: asset.aspect_ratio_category,
          },
        ];
      }
    });
  }, []);

  // Remove asset from selection
  const removeAsset = useCallback((assetId: string) => {
    setSelectedAssets((prev) => prev.filter((a) => a.id !== assetId));
  }, []);

  // Move asset up
  const moveAssetUp = useCallback((index: number) => {
    if (index === 0) return;
    setSelectedAssets((prev) => {
      const updated = [...prev];
      [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
      return updated;
    });
  }, []);

  // Move asset down
  const moveAssetDown = useCallback((index: number) => {
    setSelectedAssets((prev) => {
      if (index === prev.length - 1) return prev;
      const updated = [...prev];
      [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
      return updated;
    });
  }, []);

  // Validation
  const isValid = useMemo(() => {
    return selectedAssets.length >= 2 && selectedAssets.length <= 10;
  }, [selectedAssets]);

  // Handle save - determine the primary asset type based on selection
  const handleSave = useCallback(() => {
    if (!isValid) return;
    
    // Determine primary asset type (majority wins, default to image)
    const imageCount = selectedAssets.filter(a => a.type === "image").length;
    const videoCount = selectedAssets.filter(a => a.type === "video").length;
    const assetType: FlexibleAssetType = videoCount > imageCount ? "video" : "image";
    
    onSave({
      assets: selectedAssets,
      assetType,
    });
    onClose();
  }, [selectedAssets, isValid, onSave, onClose]);

  const isAssetSelected = useCallback(
    (assetId: string) => selectedAssets.some((a) => a.id === assetId),
    [selectedAssets]
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-5xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              Select Flexible Ad Assets
            </span>
            <Badge variant="secondary" className="font-normal">
              {selectedAssets.length}/10 selected (min 2)
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="flex gap-4 flex-1 min-h-0">
          {/* Left side - Asset library */}
          <div className="flex-1 flex flex-col min-w-0">
            {/* Search */}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search assets by filename..."
                className="pl-9"
              />
            </div>

            {/* Assets Grid */}
            <ScrollArea className="flex-1 min-h-[350px]">
              {loading ? (
                <div className="grid grid-cols-3 gap-3 p-2">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="aspect-square bg-muted animate-pulse rounded-lg" />
                  ))}
                </div>
              ) : filteredAssets.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-[350px] text-muted-foreground">
                  <ImageIcon className="h-12 w-12 mb-4" />
                  <p>No assets available</p>
                  <p className="text-sm">Upload images or videos to your creative library</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-3 p-2">
                  {filteredAssets.map((asset) => {
                    const isSelected = isAssetSelected(asset.id);
                    const isVideo = asset.creative_type === "video";
                    return (
                      <button
                        key={asset.id}
                        onClick={() => toggleAsset(asset)}
                        className={cn(
                          "relative aspect-square rounded-lg overflow-hidden border-2 transition-all",
                          isSelected
                            ? "border-primary ring-2 ring-primary/20"
                            : "border-transparent hover:border-muted-foreground/30"
                        )}
                      >
                        {resolveThumbnailUrl(signedUrlMap, asset.thumbnail_path, asset.thumbnail_url) ? (
                          <img
                            src={resolveThumbnailUrl(signedUrlMap, asset.thumbnail_path, asset.thumbnail_url)!}
                            alt={asset.filename}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full bg-muted flex items-center justify-center">
                            {isVideo ? (
                              <Video className="h-8 w-8 text-muted-foreground" />
                            ) : (
                              <ImageIcon className="h-8 w-8 text-muted-foreground" />
                            )}
                          </div>
                        )}

                        {/* Selection checkbox */}
                        <div
                          className={cn(
                            "absolute top-2 left-2 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors",
                            isSelected ? "bg-primary border-primary" : "bg-background/80 border-muted-foreground"
                          )}
                        >
                          {isSelected && (
                            <svg className="w-3 h-3 text-primary-foreground" viewBox="0 0 12 12" fill="none">
                              <path
                                d="M2 6L5 9L10 3"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </div>

                        {/* Type and aspect ratio badges */}
                        <div className="absolute top-1 right-1 flex gap-1">
                          {isVideo && (
                            <div className="bg-purple-600 text-white text-[10px] px-1.5 py-0.5 rounded font-medium flex items-center gap-0.5">
                              <Video className="h-2.5 w-2.5" />
                            </div>
                          )}
                          {asset.aspect_ratio_category && (
                            <div className="bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded font-medium">
                              {asset.aspect_ratio_category}
                            </div>
                          )}
                        </div>

                        {/* Filename on hover */}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2 opacity-0 hover:opacity-100 transition-opacity">
                          <p className="text-xs text-white truncate">{asset.filename}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Right side - Selected assets */}
          <div className="w-64 border rounded-lg p-3 flex flex-col bg-muted/30">
            <h4 className="text-sm font-medium mb-3 flex items-center justify-between">
              <span>Selected Assets</span>
              <Badge variant="outline" className="font-normal text-xs">
                {selectedAssets.length}/10
              </Badge>
            </h4>

            {selectedAssets.length === 0 ? (
              <div className="flex-1 flex items-center justify-center text-center text-muted-foreground text-sm">
                <div>
                  <Plus className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>Select at least 2 assets</p>
                  <p className="text-xs mt-1">Images and videos can be mixed</p>
                </div>
              </div>
            ) : (
              <ScrollArea className="flex-1">
                <div className="space-y-2">
                  {selectedAssets.map((asset, index) => (
                    <div
                      key={asset.id}
                      className="flex items-center gap-2 p-2 bg-background rounded border"
                    >
                      {/* Reorder buttons */}
                      <div className="flex flex-col gap-0.5">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5"
                          onClick={() => moveAssetUp(index)}
                          disabled={index === 0}
                        >
                          <ArrowUp className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5"
                          onClick={() => moveAssetDown(index)}
                          disabled={index === selectedAssets.length - 1}
                        >
                          <ArrowDown className="h-3 w-3" />
                        </Button>
                      </div>

                      {/* Thumbnail */}
                      <div className="w-10 h-10 rounded overflow-hidden flex-shrink-0 relative">
                        {asset.thumbnail_url ? (
                          <img
                            src={asset.thumbnail_url}
                            alt={asset.filename}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-muted flex items-center justify-center">
                            {asset.type === "image" ? (
                              <ImageIcon className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <Video className="h-4 w-4 text-muted-foreground" />
                            )}
                          </div>
                        )}
                        {/* Type badge on thumbnail */}
                        {asset.type === "video" && (
                          <div className="absolute bottom-0 right-0 bg-purple-600 p-0.5 rounded-tl">
                            <Video className="h-2 w-2 text-white" />
                          </div>
                        )}
                      </div>

                      {/* Filename and aspect ratio */}
                      <div className="flex-1 min-w-0">
                        <span className="text-xs truncate block">{asset.filename}</span>
                        {(asset as any).aspect_ratio_category && (
                          <span className="text-[10px] text-muted-foreground">
                            {(asset as any).aspect_ratio_category}
                          </span>
                        )}
                      </div>

                      {/* Remove button */}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 flex-shrink-0"
                        onClick={() => removeAsset(asset.id)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}

            {/* Validation message */}
            {selectedAssets.length > 0 && selectedAssets.length < 2 && (
              <p className="text-xs text-destructive mt-2">Minimum 2 assets required</p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!isValid}>
            Save Assets
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}